import { useEffect } from "react";

/**
 * Global cursor-tracking spotlight controller.
 * Updates `--mx` / `--my` CSS variables on any element with `data-spotlight`.
 *
 * Perf:
 * - Single delegated listener, rAF-throttled
 * - Skips entirely on touch / reduced-motion
 */
export const SpotlightController = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hoverMQ = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reduceMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!hoverMQ.matches || reduceMQ.matches) return;

    let raf = 0;
    let pending: { el: HTMLElement; x: number; y: number } | null = null;
    const flush = () => {
      raf = 0;
      if (!pending) return;
      pending.el.style.setProperty("--mx", `${pending.x}px`);
      pending.el.style.setProperty("--my", `${pending.y}px`);
      pending = null;
    };

    const onMove = (e: PointerEvent) => {
      const t = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-spotlight]");
      if (!t) return;
      const r = t.getBoundingClientRect();
      pending = { el: t, x: e.clientX - r.left, y: e.clientY - r.top };
      if (!raf) raf = requestAnimationFrame(flush);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return null;
};
